import { applicationsApi, ApplicationsApiTypes } from 'api'
import { URLS } from 'constants/urls'
import { Pagination } from 'ui/molecules/Pagination'
import { useApiRequest } from 'hooks/useApiRequest'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  APPLICATION_DUPLICATE,
  APPLICATION_DUPLICATE_HEAD_ITEMS,
  APPLICATION_TITLE,
} from 'texts/applications'
import { PAGINATION_DROPDOWN_LABEL } from 'texts/uiTexts'
import { Container } from 'ui/molecules/Container'
import { Table } from 'ui/molecules/Table'
import { Typography } from 'ui/atoms/Typography'
import { APPLICATION_STATUS } from 'modules/Applications/constants'
import { usePagination } from 'hooks/usePagination'
import styles from './styles.module.scss'

export const ApplicationsDuplicate = () => {
  const { id, status } = useParams()
  const [applications, setApplications] = useState<
    ApplicationsApiTypes.ApplicationListResponse['applications'] | null
  >(null)

  const navigate = useNavigate()
  const { page, pageSize } = usePagination()

  const breadcrumbList = [
    { link: `${URLS.APPLICATIONS}/${status}/${id}`, label: APPLICATION_TITLE },
  ]

  const applicationListRequest = useApiRequest((pageIndex, pageSize, id) =>
    applicationsApi.getApplicationDuplicateList(pageIndex, pageSize, id)
  )

  useEffect(() => {
    const fetchFilteredCompanyList = async () => {
      const applicationListResponse = await applicationListRequest.apiRequest(
        page - 1,
        pageSize,
        id
      )

      if (applicationListResponse) {
        setApplications(applicationListResponse.data.applications)
      }
    }
    fetchFilteredCompanyList()
  }, [page, pageSize])

  const handleRedirectToApplication = (id: string) => {
    navigate(id)
  }

  const formattedBodyItems = useMemo(
    () =>
      applications?.pageItems.map((application) => ({
        items: [
          application.companyName,
          application.submission,
          <Typography
            Tag="a"
            className={styles['email-link']}
            name="Subtitle7"
            color="blue700"
            target="_blank"
            onClick={(e) => {
              e.stopPropagation()
            }}
            href={`mailto:${application.companyEmail}`}
          >
            {application.companyEmail}
          </Typography>,
          application.phoneNumber,
          application.status === APPLICATION_STATUS.WaitingList
            ? '-'
            : application.processTime,
        ],
        onClick: () => handleRedirectToApplication(String(application.Id)),
      })),
    [applications]
  )

  const formattedHeadItems = useMemo(
    () =>
      Object.values(APPLICATION_DUPLICATE_HEAD_ITEMS).map((header) => ({
        value: header,
      })),
    []
  )

  return (
    <Container
      title={APPLICATION_DUPLICATE.TITLE}
      breadcrumbList={breadcrumbList}
      withCompanyName={false}
      currentPageLabel={APPLICATION_DUPLICATE.TITLE}
      loading={applicationListRequest.loading}
    >
      <div>
        <div className={styles.list}>
          <div className={styles.table}>
            <Table
              headItems={formattedHeadItems}
              bodyItems={formattedBodyItems}
              className={styles.thead}
            />
          </div>
        </div>
        <footer className={styles.pagination}>
          <Pagination
            defaultPage={page}
            defaultPageSize={Number(pageSize)}
            pageItemCount={applications?.pageItems.length}
            total={applications?.totalItems || 0}
            pageSizeLabel={PAGINATION_DROPDOWN_LABEL}
          />
        </footer>
      </div>
    </Container>
  )
}
